export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  category: string;
  tags: string[];
  author: string;
  date: string;
  readTime: string;
}

export interface AuthorProfile {
  name: string;
  jobTitle: string;
  description: string;
  image: string;
  sameAs: string[];
  knowsAbout: string[];
}

export const authorProfiles: Record<string, AuthorProfile> = {
  "Priya Sharma": {
    name: "Priya Sharma",
    jobTitle: "Certified Health Insurance Advisor",
    description:
      "Priya Sharma is a certified health insurance advisor with over 8 years of experience helping families across India choose the right health and travel insurance coverage. She specializes in claim assistance, cashless hospital networks, and senior citizen health plans.",
    image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=400&fit=crop&q=80",
    sameAs: [
      "https://www.linkedin.com/in/priya-sharma-insurance-advisor",
      "https://twitter.com/priyasharmaea",
    ],
    knowsAbout: [
      "Health Insurance",
      "Travel Insurance",
      "Cashless Claims",
      "Family Floater Plans",
      "Senior Citizen Health Insurance",
    ],
  },
  "Rahul Mehta": {
    name: "Rahul Mehta",
    jobTitle: "Senior Financial Planner & IRDAI Certified Advisor",
    description:
      "Rahul Mehta is a senior financial planner and IRDAI-certified insurance advisor with 12+ years in wealth management and retirement planning. He holds certifications in financial planning and regularly contributes expert commentary on term insurance and long-term investment strategies.",
    image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=400&fit=crop&q=80",
    sameAs: [
      "https://www.linkedin.com/in/rahul-mehta-financial-planner",
      "https://twitter.com/rahulmehtaea",
    ],
    knowsAbout: [
      "Term Insurance",
      "Life Insurance",
      "Retirement Planning",
      "Financial Planning",
      "Wealth Management",
      "NPS",
      "EPF",
    ],
  },
  "Neha Gupta": {
    name: "Neha Gupta",
    jobTitle: "Mutual Fund Distributor & Investment Analyst",
    description:
      "Neha Gupta is a SEBI-registered mutual fund distributor and investment analyst with expertise in SIP planning, ELSS tax-saving strategies, and equity portfolio construction. She has helped over 500 clients build disciplined investment habits and optimize their Section 80C allocations.",
    image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=400&fit=crop&q=80",
    sameAs: [
      "https://www.linkedin.com/in/neha-gupta-investment-analyst",
      "https://twitter.com/nehaguptaea",
    ],
    knowsAbout: [
      "Mutual Funds",
      "SIP Investments",
      "ELSS Tax Saving",
      "Section 80C",
      "Equity Investing",
      "Portfolio Construction",
    ],
  },
  "Amit Patel": {
    name: "Amit Patel",
    jobTitle: "Motor Insurance Specialist & Claims Expert",
    description:
      "Amit Patel is a motor insurance specialist with deep expertise in car insurance policy structuring, zero-depreciation add-ons, and claims documentation. He has processed over 2,000 motor insurance claims and is known for his practical advice on avoiding common claim rejection pitfalls.",
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&q=80",
    sameAs: [
      "https://www.linkedin.com/in/amit-patel-motor-insurance",
      "https://twitter.com/amitpatelea",
    ],
    knowsAbout: [
      "Car Insurance",
      "Motor Insurance",
      "Insurance Claims",
      "Zero Depreciation Cover",
      "Comprehensive Motor Policies",
      "TPA Coordination",
    ],
  },
};

export const blogPosts: BlogPost[] = [
  {
    id: "1",
    title: "How to Choose the Best Health Insurance Plan in India 2024",
    slug: "how-to-choose-best-health-insurance-plan-india-2024",
    excerpt: "A comprehensive guide to comparing health insurance plans, understanding waiting periods, network hospitals, and claim settlement ratios.",
    content: "Choosing the right health insurance plan is one of the most important financial decisions you will make. With over 30 insurers and hundreds of plans in India, the choice can be overwhelming. This guide breaks down the key factors you need to consider...",
    category: "Health Insurance",
    tags: ["health insurance", "comparison", "buying guide", "2024"],
    author: "Priya Sharma",
    date: "2024-01-15",
    readTime: "8 min read",
  },
  {
    id: "2",
    title: "Term Insurance vs Life Insurance: Which One Do You Need?",
    slug: "term-insurance-vs-life-insurance-which-one-need",
    excerpt: "Understand the key differences between term insurance and traditional life insurance. Learn when to choose pure protection vs investment-linked policies.",
    content: "The debate between term insurance and life insurance has been ongoing for decades. Each serves a different purpose and suits different financial profiles...",
    category: "Term Insurance",
    tags: ["term insurance", "life insurance", "comparison", "financial planning"],
    author: "Rahul Mehta",
    date: "2024-02-03",
    readTime: "6 min read",
  },
  {
    id: "3",
    title: "SIP vs Lumpsum: The Definitive Guide for Indian Investors",
    slug: "sip-vs-lumpsum-definitive-guide-indian-investors",
    excerpt: "Should you invest via SIP or lumpsum? We analyze historical data, market cycles, and psychological factors to help you decide.",
    content: "The SIP vs Lumpsum debate is one of the most frequently asked questions in mutual fund investing. Both have their merits, and the right choice depends on several factors...",
    category: "SIP Investments",
    tags: ["SIP", "mutual funds", "investment strategy", "wealth creation"],
    author: "Neha Gupta",
    date: "2024-02-18",
    readTime: "10 min read",
  },
  {
    id: "4",
    title: "Top 5 Mistakes People Make When Buying Car Insurance",
    slug: "top-5-mistakes-buying-car-insurance",
    excerpt: "Avoid these common pitfalls when purchasing or renewing your motor insurance. Save money and get better coverage.",
    content: "Car insurance is mandatory in India, but most buyers treat it as a checkbox exercise. This leads to poor coverage, higher premiums, and claim rejections...",
    category: "Car Insurance",
    tags: ["car insurance", "motor insurance", "mistakes", "savings"],
    author: "Amit Patel",
    date: "2024-03-05",
    readTime: "5 min read",
  },
  {
    id: "5",
    title: "Retirement Planning for Salaried Employees: A Complete Roadmap",
    slug: "retirement-planning-salaried-employees-complete-roadmap",
    excerpt: "Start retirement planning in your 30s with this step-by-step guide covering EPF, NPS, mutual funds, and annuity plans.",
    content: "Retirement planning often takes a backseat for salaried employees who assume their EPF and gratuity will be sufficient. However, rising inflation and increasing life expectancy make proactive planning essential...",
    category: "Retirement Planning",
    tags: ["retirement", "NPS", "EPF", "financial planning", "salaried"],
    author: "Rahul Mehta",
    date: "2024-03-20",
    readTime: "12 min read",
  },
  {
    id: "6",
    title: "How to File a Health Insurance Claim: Cashless vs Reimbursement",
    slug: "how-to-file-health-insurance-claim-cashless-reimbursement",
    excerpt: "Step-by-step guide to filing health insurance claims. Understand documentation, TPA coordination, and how to avoid common rejection reasons.",
    content: "Filing a health insurance claim can be stressful, especially during a medical emergency. Understanding the process beforehand can save you time, money, and anxiety...",
    category: "Health Insurance",
    tags: ["health insurance", "claims", "cashless", "reimbursement", "TPA"],
    author: "Amit Patel",
    date: "2024-04-02",
    readTime: "7 min read",
  },
  {
    id: "7",
    title: "ELSS Mutual Funds: The Best Tax-Saving Investment Under Section 80C",
    slug: "elss-mutual-funds-best-tax-saving-investment-section-80c",
    excerpt: "Why ELSS funds beat PPF, NSC, and tax-saving FDs. Learn about lock-in periods, returns, and how to choose the right ELSS fund.",
    content: "Among all Section 80C investments, ELSS mutual funds stand out for their combination of tax benefits, shortest lock-in period, and potential for high returns...",
    category: "Mutual Funds",
    tags: ["ELSS", "tax saving", "Section 80C", "mutual funds", "investment"],
    author: "Neha Gupta",
    date: "2024-04-15",
    readTime: "6 min read",
  },
  {
    id: "8",
    title: "Travel Insurance: Why You Should Never Skip It for International Trips",
    slug: "travel-insurance-never-skip-international-trips",
    excerpt: "Medical emergencies abroad can cost lakhs. Learn what travel insurance covers, how much you need, and which plans work best for USA, Europe, and Southeast Asia.",
    content: "A medical emergency in the United States can cost upwards of $50,000. Without travel insurance, this could wipe out years of savings in a single incident...",
    category: "Travel Insurance",
    tags: ["travel insurance", "international travel", "medical emergency", "schengen"],
    author: "Priya Sharma",
    date: "2024-05-01",
    readTime: "5 min read",
  },
];

export const blogCategories = [
  "All",
  "Health Insurance",
  "Term Insurance",
  "Life Insurance",
  "Car Insurance",
  "Travel Insurance",
  "Corporate Insurance",
  "Mutual Funds",
  "SIP Investments",
  "Retirement Planning",
  "Tax Saving",
  "Personal Finance",
];